/* ============================================================
   PC構成仕様書 PDF出力（jsPDF ネイティブ描画方式）
   - jsPDFでテキスト・線・画像を直接描画
   - 日本語フォント（Kosugi）をCDNから取得しBase64で埋め込む
   - テキスト選択可能・ハイパーリンク可能
   ============================================================ */
(function () {
  'use strict';

  // 二重実行ガード: セクション版とスニペット版が同時に描画されて
  // 同一srcのscriptが2回実行されると、clickリスナー二重登録でPDFが2回生成されるのを防ぐ
  if (window.__pcSpecPdfInitialized) return;
  window.__pcSpecPdfInitialized = true;

  var JSPDF_CDN = 'https://cdn.jsdelivr.net/npm/jspdf@2.5.1/dist/jspdf.umd.min.js';
  var QRCODE_CDN = 'https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.min.js';

  // 注意事項の固定リスト（短縮3項目版）
  var DEFAULT_NOTES = [
    '本書は見積書・価格保証書ではありません。',
    '価格はPDF作成時点の参考価格です。',
    '在庫・納期・保証・送料は商品ページをご確認ください。',
  ];

  // LINE公式アカウントURL（QRコードに埋め込む）
  var LINE_URL = 'https://page.line.me/smolabpc?oat_content=url&openQrModal=true';
  // 軽量日本語フォント（Kosugi、Google Fonts OFL）— Shopify assets経由で配信
  var FONT_URL = window.PC_SPEC_PDF_FONT_URL ||
    'https://fonts.gstatic.com/s/kosugi/v19/pxiFyp4_v8FCjlI4NA.ttf';
  var FONT_NAME = 'Kosugi';
  var FONT_FILE = 'Kosugi-Regular.ttf';

  var IMG_WAIT_TIMEOUT = 5000;
  var PDF_OVERALL_TIMEOUT = 60000;

  // ---------- ライブラリ・フォント読み込み ----------
  function loadScript(src, isReady, timeoutMs) {
    return new Promise(function (resolve, reject) {
      if (isReady()) return resolve();
      var script = document.createElement('script');
      script.src = src;
      script.async = true;
      var timeoutId = setTimeout(function () {
        reject(new Error('Script load timeout: ' + src));
      }, timeoutMs || 15000);
      script.onload = function () {
        clearTimeout(timeoutId);
        if (isReady()) resolve();
        else reject(new Error('Script loaded but not ready: ' + src));
      };
      script.onerror = function () {
        clearTimeout(timeoutId);
        reject(new Error('Script load failed: ' + src));
      };
      document.head.appendChild(script);
    });
  }

  // ---------- PDF.js（PDF→JPG画像変換用。画像出力ボタン押下時のみ遅延読込） ----------
  var PDFJS_CDN = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.min.js';
  var PDFJS_WORKER_CDN = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
  var pdfjsPromise = null;
  function loadPdfJs() {
    var isReady = function () { return !!(window.pdfjsLib && window.pdfjsLib.getDocument); };
    if (isReady()) return Promise.resolve();
    if (pdfjsPromise) return pdfjsPromise;
    pdfjsPromise = loadScript(PDFJS_CDN, isReady).then(function () {
      window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
    }).catch(function (err) {
      pdfjsPromise = null;
      throw err;
    });
    return pdfjsPromise;
  }

  var jspdfPromise = null;
  function loadJsPdf() {
    var isReady = function () { return !!(window.jspdf && window.jspdf.jsPDF); };
    if (isReady()) return Promise.resolve();
    if (jspdfPromise) return jspdfPromise;
    jspdfPromise = loadScript(JSPDF_CDN, isReady).catch(function (err) {
      jspdfPromise = null;
      throw err;
    });
    return jspdfPromise;
  }

  var qrCodePromise = null;
  function loadQrCode() {
    var isReady = function () { return typeof window.qrcode === 'function'; };
    if (isReady()) return Promise.resolve();
    if (qrCodePromise) return qrCodePromise;
    qrCodePromise = loadScript(QRCODE_CDN, isReady).catch(function (err) {
      qrCodePromise = null;
      throw err;
    });
    return qrCodePromise;
  }

  // フォントBase64キャッシュ（ページ内）
  var fontBase64Cache = null;
  var fontPromise = null;
  function loadJapaneseFontBase64() {
    if (fontBase64Cache) return Promise.resolve(fontBase64Cache);
    if (fontPromise) return fontPromise;
    fontPromise = fetch(FONT_URL, { mode: 'cors' })
      .then(function (res) {
        if (!res.ok) throw new Error('Font HTTP ' + res.status);
        return res.arrayBuffer();
      })
      .then(function (buf) {
        var bytes = new Uint8Array(buf);
        var binary = '';
        var CHUNK = 8192;
        for (var i = 0; i < bytes.length; i += CHUNK) {
          binary += String.fromCharCode.apply(null, bytes.subarray(i, i + CHUNK));
        }
        fontBase64Cache = btoa(binary);
        return fontBase64Cache;
      })
      .catch(function (err) {
        fontPromise = null; // 失敗時は次回再試行可能に
        throw err;
      });
    return fontPromise;
  }

  function registerJapaneseFont(pdf, base64) {
    pdf.addFileToVFS(FONT_FILE, base64);
    pdf.addFont(FONT_FILE, FONT_NAME, 'normal');
    pdf.setFont(FONT_NAME, 'normal');
  }

  // ---------- ユーティリティ ----------
  // デバッグログ（window.__PC_SPEC_PDF_DEBUG__ = true で出力）
  function dlog() {
    if (window.__PC_SPEC_PDF_DEBUG__) {
      try { console.log.apply(console, arguments); } catch (e) {}
    }
  }

  // 簡易トースト（数秒で消える非ブロッキング通知）
  function showToast(message, ms) {
    try {
      var el = document.createElement('div');
      el.textContent = message;
      el.style.cssText = [
        'position:fixed', 'left:50%', 'bottom:24px', 'transform:translateX(-50%)',
        'background:rgba(31,41,55,0.92)', 'color:#fff',
        'padding:10px 16px', 'border-radius:6px',
        'font-size:13px', 'z-index:99999', 'max-width:90vw',
        'box-shadow:0 4px 12px rgba(0,0,0,0.2)',
        'pointer-events:none'
      ].join(';');
      document.body.appendChild(el);
      setTimeout(function () {
        if (el.parentNode) el.parentNode.removeChild(el);
      }, ms || 4000);
    } catch (e) { /* ignore */ }
  }

  // URLサニタイズ（http/httpsのみ許可）
  function sanitizeUrl(url) {
    if (!url) return null;
    try {
      var u = new URL(url, window.location.href);
      if (u.protocol === 'http:' || u.protocol === 'https:') return u.href;
    } catch (e) { /* invalid URL */ }
    return null;
  }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }
  function formatDateJP(d) {
    return d.getFullYear() + '年' + pad2(d.getMonth() + 1) + '月' + pad2(d.getDate()) + '日';
  }
  function formatDateTimeJP(d) {
    return formatDateJP(d) + ' ' + pad2(d.getHours()) + ':' + pad2(d.getMinutes());
  }
  function ymd(d) {
    return d.getFullYear() + pad2(d.getMonth() + 1) + pad2(d.getDate());
  }
  // 数字+円表記に統一（¥や￥は使わない）
  function formatYen(n) {
    return Math.round(n).toLocaleString('ja-JP') + '円';
  }

  /**
   * 表記ゆれを正規化する
   * NVME→NVMe / Windows11→Windows 11 / Ryzen5→Ryzen 5 / PCIE→PCIe / Bluetooth5.3→Bluetooth 5.3 等
   */
  function normalizeText(text) {
    if (!text) return text;
    var t = String(text);
    // WiFi / WIFI / Wi Fi → Wi-Fi（大小文字非依存）
    // 末尾の \b は付けない：後続が数字のケース（例: WiFi6E）でも確実に変換するため
    t = t.replace(/\bWi[\s-]?Fi/gi, 'Wi-Fi');
    // NVME → NVMe（大小文字非依存）
    t = t.replace(/\bNVME\b/gi, 'NVMe');
    // PCIE / PCI-E → PCIe
    t = t.replace(/\bPCIE\b/gi, 'PCIe').replace(/\bPCI-E\b/gi, 'PCIe');
    // Windows11 → Windows 11
    t = t.replace(/\bWindows(\d+)/gi, 'Windows $1');
    // Ryzen5/7/9 → Ryzen 5/7/9
    t = t.replace(/\bRyzen([0-9])/gi, 'Ryzen $1');
    // Bluetooth5.3 → Bluetooth 5.3
    t = t.replace(/\bBluetooth(\d)/gi, 'Bluetooth $1');
    // Wi-Fi6E → Wi-Fi 6E
    t = t.replace(/\b(Wi-?Fi)(\d)/gi, '$1 $2');
    // 「PCIe接続Wi-Fi」→「PCIe接続 Wi-Fi」
    t = t.replace(/(PCIe接続)(Wi-Fi)/g, '$1 $2');
    // 「Wi-Fi 6E、Bluetooth 5.3」→「Wi-Fi 6E / Bluetooth 5.3」
    t = t.replace(/(Wi-Fi\s*[\d.]+E?)\s*[、,]\s*(Bluetooth)/g, '$1 / $2');
    // ※「Gold認証→80PLUS Gold認証」の自動付与は廃止（実機が80PLUS認証とは限らず事実と異なる場合があるため、元の表記のまま表示する）
    // サイズ表記正規化
    t = t.replace(/幅\s*[×xX]\s*高さ\s*[×xX]\s*奥行\s*[：:]\s*(\d+)\s*[×xX]\s*(\d+)\s*[×xX]\s*(\d+)\s*mm/g,
      '幅$1 × 高さ$2 × 奥行$3mm');
    // CPU型番の X3D（例: 9800X3D）を寸法表記と誤認して「9800 × 3D」に変換しないよう、
    // 後続に「数字+D」が続く場合（=X3D系サフィックス）は除外する
    t = t.replace(/(\d)\s*[xX]\s*(\d)(?!\d*[dD])/g, '$1 × $2');
    t = t.replace(/  +/g, ' ');

    // ※ NBSP（U+00A0）による分割防止は撤回。テーブル内テキストが overflow して
    //    切れる原因になっていたため、通常空白を維持し、splitTextToSize に任せる。
    return t;
  }

  function getQuantity() {
    var inputs = document.querySelectorAll(
      'product-info input[name="quantity"], form[action*="/cart/add"] input[name="quantity"], input[name="quantity"].quantity__input'
    );
    for (var i = 0; i < inputs.length; i++) {
      var v = parseInt(inputs[i].value, 10);
      if (!isNaN(v) && v >= 1) return v;
    }
    return 1;
  }

  function withTimeout(promise, ms, label) {
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () {
        reject(new Error('Timeout: ' + (label || 'operation') + ' (' + ms + 'ms)'));
      }, ms);
      promise.then(
        function (v) { clearTimeout(t); resolve(v); },
        function (e) { clearTimeout(t); reject(e); }
      );
    });
  }

  // ---------- 画像処理 ----------
  function imageUrlToDataURL(url, opts) {
    opts = opts || {};
    return new Promise(function (resolve) {
      if (!url) return resolve(null);
      var img = new Image();
      img.crossOrigin = 'anonymous';
      var settled = false;
      var done = function (v) { if (settled) return; settled = true; resolve(v); };
      img.onload = function () {
        try {
          var canvas = document.createElement('canvas');
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          var ctx = canvas.getContext('2d');
          if (opts.whiteBg) {
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
          }
          ctx.drawImage(img, 0, 0);
          done({
            dataUrl: canvas.toDataURL('image/png'),
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        } catch (e) {
          done(null);
        }
      };
      img.onerror = function () { done(null); };
      setTimeout(function () { done(null); }, IMG_WAIT_TIMEOUT + 2000);
      img.src = url;
    });
  }

  // ---------- オプション抽出（自前カスタマイズ UI pc-customize.js） ----------
  /**
   * 商品ページに表示されている「現在の合計価格」を取得（オプション込み）
   * 個別オプション価格が取れなかったときのフォールバック用
   */
  function getCurrentDisplayedTotalPrice() {
    // 0) 自前カスタマイズ UI (pc-customize.js) が有効なら、その合計 (本体 + 追加料金) を最優先
    if (window.SmolabCustomize && window.SmolabCustomize.isActive) {
      try {
        var smolabTotal = window.SmolabCustomize.getTotalYen();
        if (smolabTotal > 0) return smolabTotal;
      } catch (e) { /* fallthrough */ }
    }

    // 1) 商品ページの価格表示
    // セール中は販売価格(.price-item--sale)を優先。.price-item--regular を先に見ると
    // 打ち消し線付きの割引前価格(compare_at)を拾い、PDF合計が過大になるため。
    var saleEls = document.querySelectorAll('.price__sale .price-item--sale, .price-item--sale');
    for (var s = 0; s < saleEls.length; s++) {
      if (saleEls[s].offsetParent === null) continue; // 非セール時は非表示
      var st = (saleEls[s].textContent || '').replace(/\s+/g, '');
      var sm = st.match(/[¥￥]([\d,]+)/);
      if (sm) {
        var sn = parseInt(sm[1].replace(/,/g, ''), 10);
        if (sn > 0) return sn;
      }
    }
    var priceSel = [
      '.product__price .price-item--regular',
      '.price__regular .price-item--regular',
      '[id^="price-template"] .price-item--regular',
      '.price-item--regular'
    ].join(', ');
    var priceEls = document.querySelectorAll(priceSel);
    for (var i = 0; i < priceEls.length; i++) {
      if (priceEls[i].offsetParent === null) continue; // 非表示（.price__sale内の元値等）はスキップ
      var t = (priceEls[i].textContent || '').replace(/\s+/g, '');
      var pm = t.match(/[¥￥]([\d,]+)/);
      if (pm) {
        var nn = parseInt(pm[1].replace(/,/g, ''), 10);
        if (nn > 0) return nn;
      }
    }
    return 0;
  }

  function collectSelectedOptions() {
    var results = [];

    // 自前カスタマイズ UI (pc-customize.js) が有効なら、その選択内容を
    // { group: 軸名, value: ラベル, price: 追加料金(円) } 形式で返す (ベース=変更なし も含む全軸)
    if (window.SmolabCustomize && window.SmolabCustomize.isActive) {
      try {
        var smolabSel = window.SmolabCustomize.getSelection() || [];
        smolabSel.forEach(function (s) {
          if (!s || !s.group || !s.value) return;
          results.push({ group: s.group, value: s.value, price: s.price || 0 });
        });
      } catch (e) {
        dlog('[pc-spec-pdf] SmolabCustomize.getSelection failed', e);
      }
    }
    dlog('[pc-spec-pdf] collected options:', results);
    return results;
  }

  // ---------- QRコード生成 ----------
  /**
   * URLからQRコードのPNG DataURLを生成する。
   * qrcode-generator がロード済み前提（loadQrCode()で事前ロード）
   */
  function generateQrDataUrl(url, sizePx) {
    if (!url || typeof window.qrcode !== 'function') return null;
    try {
      // type 0 = 自動、error correction 'M'
      var qr = window.qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      var modules = qr.getModuleCount();
      var canvas = document.createElement('canvas');
      var px = sizePx || 200;
      var scale = Math.floor(px / modules) || 1;
      var size = scale * modules;
      canvas.width = size;
      canvas.height = size;
      var ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.fillStyle = '#000000';
      for (var r = 0; r < modules; r++) {
        for (var c = 0; c < modules; c++) {
          if (qr.isDark(r, c)) {
            ctx.fillRect(c * scale, r * scale, scale, scale);
          }
        }
      }
      return canvas.toDataURL('image/png');
    } catch (e) {
      return null;
    }
  }

  // ---------- オプション → 仕様反映 ----------
  /**
   * オプションの値テキストから「に変更」「を追加」「に変更します」等の指示語を取り除く
   */
  function cleanOptionValueForSpec(value) {
    if (!value) return value;
    var t = String(value);
    t = t.replace(/(に\s*変更します|に\s*変更|に\s*更新|へ\s*変更|を\s*追加します|を\s*追加|を\s*変更)/g, '');
    t = t.replace(/\s+/g, ' ').trim();
    return normalizeText(t);
  }

  /**
   * グループ名を正規化（小文字化、括弧自体を除去するが括弧内の中身は保持）
   * 例: 「追加ストレージ（HDD）」→「追加ストレージhdd」
   *     「メモリ（DDR4）」→「メモリddr4」
   */
  function normalizeGroupName(name) {
    if (!name) return '';
    var g = String(name).toLowerCase();
    // 括弧自体は除去するが中身（HDD/SSD/DDR4 等）は保持する
    g = g.replace(/[（()）]/g, '');
    // 空白と記号除去
    g = g.replace(/[\s 　・]/g, '');
    return g;
  }

  /**
   * 辞書ベースのグループ→仕様マッピング。
   * パターンマッチが順に評価され、最初にマッチした行が採用される。
   * type: 'override' = 既存仕様行を上書き / 'append' = 保証行の前に追加
   */
  var GROUP_SPEC_MAP = [
    // 追加系（より具体的なものを先に）
    { pattern: /追加.*hdd|追加hdd/,            type: 'append',   label: '追加HDD' },
    { pattern: /追加.*ssd|追加ssd|追加nvme/,    type: 'append',   label: '追加SSD' },
    { pattern: /追加ストレージ|追加storage/,    type: 'append',   label: '追加ストレージ' },
    // 上書き系
    { pattern: /cpu.*(クーラー|cooler|fan)/,   type: 'override', label: 'CPUクーラー' },
    { pattern: /^メモリ|^memory|^ram|^ddr/,    type: 'override', label: 'メモリ' },
    { pattern: /^ストレージ|^ssd|^nvme|^m\.2/, type: 'override', label: 'SSD' },
    { pattern: /電源|psu|power/,                type: 'override', label: '電源ユニット' },
    { pattern: /^os|オペレーティング|windows/,  type: 'override', label: 'OS' },
    { pattern: /無線|wifi|wi-?fi|bluetooth/,    type: 'override', label: '無線' },
    { pattern: /pcケース|ケース|chassis|case/,  type: 'override', label: 'PCケース' },
    { pattern: /マザーボード|motherboard/,      type: 'override', label: 'マザーボード' },
    { pattern: /^gpu|グラフィック/,             type: 'override', label: 'GPU' },
    { pattern: /^cpu(?!.*クーラー)/,            type: 'override', label: 'CPU' },
  ];

  function mapOptionToSpec(group) {
    var g = normalizeGroupName(group);
    if (!g) return null;
    for (var i = 0; i < GROUP_SPEC_MAP.length; i++) {
      if (GROUP_SPEC_MAP[i].pattern.test(g)) {
        var entry = GROUP_SPEC_MAP[i];
        if (entry.type === 'append') return { appendLabel: entry.label };
        return { overrideLabel: entry.label };
      }
    }
    return null;
  }

  // 最終仕様の表示順
  var SPEC_ORDER = [
    '型番', 'CPU', 'GPU', 'マザーボード', 'CPUクーラー',
    'メモリ', 'SSD', '追加SSD', '追加HDD',
    '電源ユニット', 'OS', '無線', 'PCケース',
    '端子', '商品サイズ', '付属品', '保証'
  ];

  /**
   * 標準仕様 (spec配列) にオプション選択を反映した「選択後の最終仕様」を生成
   * 表示順は SPEC_ORDER で固定
   */
  function buildFinalSpec(baseSpec, options) {
    // ベースの値はすべて normalizeText で表記統一
    var mapBy = {};
    baseSpec.forEach(function (r) {
      mapBy[r.label] = { label: r.label, value: normalizeText(r.value) };
    });

    options.forEach(function (o) {
      var mapping = mapOptionToSpec(o.group);
      if (!mapping) return;
      var cleanValue = cleanOptionValueForSpec(o.value);
      if (!cleanValue) return;

      var label = mapping.overrideLabel || mapping.appendLabel;
      mapBy[label] = { label: label, value: cleanValue };
    });

    // SPEC_ORDER の順で並べる。SPEC_ORDER外の項目は末尾に追加
    var ordered = [];
    SPEC_ORDER.forEach(function (label) {
      if (mapBy[label]) {
        ordered.push(mapBy[label]);
        delete mapBy[label];
      }
    });
    // 残ったもの（順序未指定）は末尾に
    Object.keys(mapBy).forEach(function (k) { ordered.push(mapBy[k]); });

    return ordered;
  }

  // ---------- データ収集（テンプレから） ----------
  function collectPdfData(btn) {
    var tpl = document.getElementById('pc-spec-pdf-template');
    if (!tpl) throw new Error('PDFテンプレートが見つかりません');

    var spec = [];
    tpl.querySelectorAll('.psp-spec__table tr').forEach(function (tr) {
      var th = tr.querySelector('th');
      var td = tr.querySelector('td');
      if (!th || !td) return;
      spec.push({
        label: (th.textContent || '').trim(),
        value: (td.textContent || '').trim(),
      });
    });

    var notes = [];
    tpl.querySelectorAll('.psp-footer__notes li').forEach(function (li) {
      var t = (li.textContent || '').trim();
      if (t) notes.push(t);
    });
    // テンプレ取得失敗・件数不足時はデフォルトで補完
    if (notes.length < DEFAULT_NOTES.length) {
      notes = DEFAULT_NOTES.slice();
    }
    // 「見積書・価格保証書ではありません」相当文言がなければ先頭に挿入（テンプレ依存防止）
    var hasNoQuoteNote = notes.some(function (n) {
      return /見積書|価格保証/.test(n);
    });
    if (!hasNoQuoteNote) {
      notes.unshift(DEFAULT_NOTES[0]);
    }
    // 件数を6項目に強制制限（テンプレ側が増えてもPDFは6項目固定）
    notes = notes.slice(0, DEFAULT_NOTES.length);

    var shopLines = [];
    tpl.querySelectorAll('.psp-shop__line').forEach(function (el) {
      var t = (el.textContent || '').trim();
      if (t) shopLines.push(t);
    });
    var shopNameEl = tpl.querySelector('.psp-shop__name');

    var logoImg = tpl.querySelector('.psp-logo');
    var sealImg = tpl.querySelector('.psp-seal');
    var productImg = tpl.querySelector('.psp-summary__image img');

    var qty = getQuantity();
    var unitPrice = parseInt(btn.dataset.unitPrice, 10) || 0;
    var options = collectSelectedOptions();
    var optionsTotal = options.reduce(function (s, o) { return s + (o.price || 0); }, 0);

    // フォールバック: 個別価格が全て0、かつ数量1のとき、商品ページの表示合計から逆算
    // (数量2以上のときは表示合計が単価/小計どちらか判別不能のため発動しない)
    var displayedTotal = getCurrentDisplayedTotalPrice();
    dlog('[pc-spec-pdf] unitPrice:', unitPrice, 'optionsTotal(per-option):', optionsTotal, 'displayedTotal:', displayedTotal, 'qty:', qty);
    if (optionsTotal === 0 && qty === 1 && displayedTotal > unitPrice) {
      optionsTotal = displayedTotal - unitPrice;
      dlog('[pc-spec-pdf] using displayedTotal fallback. optionsTotal:', optionsTotal);
    } else if (optionsTotal === 0 && qty > 1 && displayedTotal > unitPrice) {
      dlog('[pc-spec-pdf] WARN: skipping displayedTotal fallback because qty>1 makes the source ambiguous');
    }

    var perUnit = unitPrice + optionsTotal;
    var total = perUnit * qty;

    // オプションを反映した「選択後の最終仕様」
    var finalSpec = buildFinalSpec(spec, options);
    // オプション選択値もnormalize（表示用）
    var normalizedOptions = options.map(function (o) {
      return { group: o.group, value: cleanOptionValueForSpec(o.value), price: o.price };
    });

    return {
      now: new Date(),
      shopName: shopNameEl ? shopNameEl.textContent.trim() : 'SMO-LAB',
      shopLines: shopLines,
      publisher: '株式会社スモールラボ',
      invoiceLine: '購入後の領収書に記載',
      logoUrl: logoImg ? logoImg.src : '',
      sealUrl: sealImg ? sealImg.src : '',
      productTitle: (tpl.querySelector('.psp-summary__name') || {}).textContent || '',
      productImageUrl: productImg ? productImg.src : '',
      productSku: btn.dataset.sku || '',
      productUrl: btn.dataset.productUrl || '',
      quantity: qty,
      unitPrice: unitPrice,
      optionsTotal: optionsTotal,
      grandTotal: total,
      options: normalizedOptions,
      spec: finalSpec,
      notes: notes,
    };
  }

  // ---------- jsPDF ネイティブ描画 ----------
  function wrapText(pdf, text, maxWidth) {
    return pdf.splitTextToSize(text || '', maxWidth);
  }

  /**
   * テキストを指定幅でラップし、各行の幅がmaxWidthを超える場合はフォントサイズを縮小して再ラップ。
   * 仕様テキストが省略されたり overflow したりするのを防ぐ。
   * 戻り値: { lines: string[], fontSize: number }
   */
  function fitTextToWidth(pdf, text, maxWidth, baseFontSize, minFontSize) {
    var fs = baseFontSize;
    var minFs = minFontSize || 5.5;
    pdf.setFontSize(fs);
    var lines = wrapText(pdf, text, maxWidth);

    // 各行の実幅をチェック
    var fits = true;
    for (var i = 0; i < lines.length; i++) {
      if (pdf.getTextWidth(lines[i]) > maxWidth + 0.5) { fits = false; break; }
    }

    // 入りきらない場合、フォントサイズを縮小して再試行
    while (!fits && fs > minFs) {
      fs = Math.max(minFs, fs - 0.5);
      pdf.setFontSize(fs);
      lines = wrapText(pdf, text, maxWidth);
      fits = true;
      for (var j = 0; j < lines.length; j++) {
        if (pdf.getTextWidth(lines[j]) > maxWidth + 0.5) { fits = false; break; }
      }
      if (fs === minFs) break;
    }
    return { lines: lines, fontSize: fs };
  }

  /**
   * 現在のY位置に neededHeight が収まらない場合の処理。
   *  - ctx.singlePage が true（デフォルト）の場合: 改ページせずそのまま y を返す（押し込み）
   *  - ctx.singlePage が false の場合: 新ページを開いて topY を返す
   * 収まる場合は y をそのまま返す。
   * ctx: { pageH, marginTop, marginBottom, footerReserved, singlePage }
   */
  function ensureSpace(pdf, y, neededHeight, ctx) {
    var maxY = ctx.pageH - ctx.marginBottom - ctx.footerReserved;
    if (y + neededHeight > maxY) {
      if (ctx.singlePage) {
        // 1ページ厳守モード: 改ページせず警告のみ
        dlog('[pc-spec-pdf] WARN: content overflows single page. y:', y, 'needed:', neededHeight, 'maxY:', maxY);
        return y;
      }
      pdf.addPage();
      pdf.setFont(FONT_NAME, 'normal');
      return ctx.marginTop;
    }
    return y;
  }

  // PDFドキュメントを生成して返す（保存は呼び出し側で行う。PDF/JPG出力の共通部）
  async function renderPdfNative(data) {
    var jsPDF = window.jspdf.jsPDF;
    var pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait', compress: true });

    // フォント・QRコードライブラリを並行ロード
    var fontBase64 = await withTimeout(loadJapaneseFontBase64(), 15000, 'font load');
    registerJapaneseFont(pdf, fontBase64);
    // QRコードは失敗しても続行
    try { await withTimeout(loadQrCode(), 8000, 'qrcode load'); } catch (e) { dlog('[pc-spec-pdf] qrcode load failed:', e); }

    // 画像取得（並行）
    var imgResults = await Promise.all([
      data.logoUrl ? imageUrlToDataURL(data.logoUrl, { whiteBg: true }) : Promise.resolve(null),
      data.sealUrl ? imageUrlToDataURL(data.sealUrl, { whiteBg: false }) : Promise.resolve(null),
      data.productImageUrl ? imageUrlToDataURL(data.productImageUrl, { whiteBg: true }) : Promise.resolve(null),
    ]);
    var logoImg = imgResults[0];
    var sealImg = imgResults[1];
    var productImg = imgResults[2];

    // 必須画像（ロゴ・商品画像）の取得失敗を集計してトースト通知
    var imgWarnings = [];
    if (data.logoUrl && !logoImg) imgWarnings.push('ロゴ');
    if (data.productImageUrl && !productImg) imgWarnings.push('商品画像');
    // sealはオプショナル（404でも警告しない）
    if (imgWarnings.length > 0) {
      showToast('PDF生成: ' + imgWarnings.join('・') + ' の取得に失敗しました（プレビューは続行）');
    }

    // A4縦: 210×297mm  /  上マージン 8mm, 下マージン 10mm（印刷見切れ防止）, 左右 8mm
    var PW = 210, PH = 297, M = 8, MB = 10;
    var contentW = PW - M * 2;
    var y = M;

    // QRブロック寸法（注意事項計算で参照するため冒頭で定義）
    var qrSize = 20;
    var qrLabelH = 3;
    var qrBlockH = qrSize + qrLabelH;
    var qrGap = 2;

    // フッター（QR + URLブロック）の予約高さ。注意事項は最終仕様の右に並列配置するため除外
    var footerReserved = qrBlockH + 6;  // QRブロック + 余白
    var pageCtx = {
      pageH: PH, marginTop: M, marginBottom: MB,
      footerReserved: footerReserved,
      singlePage: true
    };

    // ===== ヘッダー (ロゴ拡大、URL/メアド分割禁止) =====
    var headerTop = y;
    var leftX = M;
    var rightX = PW - M;

    // ロゴ拡大（18→25mm、1.4倍）
    var logoSize = 25;
    if (logoImg) {
      var lw = logoSize, lh = lw * (logoImg.height / logoImg.width);
      if (lh > logoSize) { lh = logoSize; lw = lh * (logoImg.width / logoImg.height); }
      pdf.addImage(logoImg.dataUrl, 'PNG', leftX, headerTop, lw, lh);
    }

    // 店舗情報テキスト（URL/メアドは分割せず単一行で描画）
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(0, 0, 0);
    var shopTextX = leftX + (logoImg ? logoSize + 5 : 0);
    var shopY = headerTop + 4;
    pdf.text(data.shopName, shopTextX, shopY);

    pdf.setFontSize(8);
    pdf.setTextColor(70, 70, 70);
    // shopLines は URL/メアドを含む。pdf.text() に直接渡すと自動折返しはされないので分割崩れは出ない。
    // ただし、長すぎる場合に右側のmetaと重ならないよう、最大幅を計算してtruncate。
    var sealSize = 14;
    var shopMaxX = rightX - 56;   // 右側メタとの境界（meta幅約50mm + 余白）
    var shopMaxW = shopMaxX - shopTextX;
    data.shopLines.forEach(function (line, i) {
      // メールアドレス・URL（ASCIIのみ）は欧文フォントで描画し、字間の乱れ・不自然な空白を防ぐ
      var isLatin = /^[\x20-\x7E]+$/.test(line);
      pdf.setFont(isLatin ? 'helvetica' : FONT_NAME, 'normal');
      var displayLine = line;
      // 文字列幅が最大幅を超えるなら末尾を省略
      while (pdf.getTextWidth(displayLine) > shopMaxW && displayLine.length > 0) {
        displayLine = displayLine.slice(0, -1);
      }
      if (displayLine !== line) {
        // 省略する場合は末尾「…」
        while (pdf.getTextWidth(displayLine + '…') > shopMaxW && displayLine.length > 0) {
          displayLine = displayLine.slice(0, -1);
        }
        displayLine = displayLine + '…';
      }
      pdf.text(displayLine, shopTextX, shopY + 4.6 + i * 3.8);
    });
    pdf.setFont(FONT_NAME, 'normal');

    // 右上: 作成日時・発行元・適格請求書対応（ラベル右揃え + 値左揃え でカラム整列）
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(7.5);
    var metaY = headerTop + 3.5;
    var metaRows = [
      { k: '作成日時', v: formatDateTimeJP(data.now), sep: '：' },
      { k: '発行元', v: data.publisher, sep: '：' },
      { k: '適格請求書対応', v: data.invoiceLine, sep: '：' },
    ];
    var metaRightX = rightX - (sealImg ? sealSize + 3 : 0);
    // 「ラベル：値」の単一文字列で右端揃え（コロン1文表記）
    metaRows.forEach(function (row, i) {
      var yy = metaY + i * 3.6;
      var line = row.k + (row.sep || '：') + row.v;
      var lw = pdf.getTextWidth(line);
      pdf.setTextColor(40, 40, 40);
      pdf.text(line, metaRightX - lw, yy);
    });

    // 印影
    if (sealImg) {
      pdf.addImage(sealImg.dataUrl, 'PNG', rightX - sealSize, headerTop + 4, sealSize, sealSize);
    }

    // ヘッダー下の線（ロゴ高さに合わせて少し下げる）
    y = headerTop + logoSize + 2;
    pdf.setDrawColor(20, 20, 20);
    pdf.setLineWidth(0.4);
    pdf.line(M, y, PW - M, y);

    // ===== タイトル =====
    y += 5;
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(14);
    pdf.setTextColor(20, 20, 20);
    // タイトルは字間調整・均等割付を行わず素のまま中央配置（「PC構成 仕様書」のような空白を防ぐ）
    var title = 'PC構成仕様書';
    var titleW = pdf.getTextWidth(title);
    pdf.text(title, (PW - titleW) / 2, y, { charSpace: 0 });
    y += 2;
    pdf.setDrawColor(200, 200, 200);
    pdf.setLineWidth(0.15);
    pdf.line(M, y, PW - M, y);
    y += 3;

    // ===== 商品サマリー（画像 + 型番/数量2列表 + 価格3列表） =====
    var summaryTop = y;
    var imgBoxW = 50, imgBoxH = 38;
    var infoX = M + imgBoxW + 5;
    var infoW = contentW - imgBoxW - 5;

    // 商品画像
    if (productImg) {
      var aspect = productImg.height / productImg.width;
      var w = imgBoxW, h = w * aspect;
      if (h > imgBoxH) { h = imgBoxH; w = h / aspect; }
      var imgX = M + (imgBoxW - w) / 2;
      var imgY = summaryTop + (imgBoxH - h) / 2;
      pdf.addImage(productImg.dataUrl, 'PNG', imgX, imgY, w, h);
    }
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.15);
    pdf.rect(M, summaryTop, imgBoxW, imgBoxH);

    // (0) 商品名（型番・数量と同じ枠スタイル：上にラベル行、下に値）
    // ※商品名の表記は normalize しない（回帰防止）
    var nameTop = summaryTop;
    var nameHeaderH = 5;
    // ラベル行（グレー背景）
    pdf.setFillColor(243, 244, 246);
    pdf.rect(infoX, nameTop, infoW, nameHeaderH, 'F');
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.12);
    pdf.rect(infoX, nameTop, infoW, nameHeaderH);
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(80, 80, 80);
    pdf.text('商品名', infoX + 1.8, nameTop + 3.4);
    // 値行（枠内2行まで。収まらなければ 9→7pt に縮小して全文を2行に収める）
    var nameFs = 9;
    var nameLines;
    while (true) {
      pdf.setFontSize(nameFs);
      nameLines = wrapText(pdf, data.productTitle, infoW - 3.6);
      if (nameLines.length <= 2 || nameFs <= 7) break;
      nameFs -= 0.5;
    }
    var nameShown = Math.min(nameLines.length, 2);
    var nameLineH = Math.max(3.6, nameFs * 0.46);
    var nameValTop = nameTop + nameHeaderH;
    var nameValH = nameShown * nameLineH + 2.4;
    pdf.rect(infoX, nameValTop, infoW, nameValH);
    pdf.setTextColor(20, 20, 20);
    nameLines.slice(0, 2).forEach(function (line, i) {
      pdf.text(line, infoX + 1.8, nameValTop + 3.2 + i * nameLineH);
    });

    // (1) 型番・数量 2列表
    var infoTblY = nameValTop + nameValH + 1.5;
    var halfW = infoW / 2;
    var infoRowH = 5;

    // ヘッダー
    pdf.setFillColor(243, 244, 246);
    pdf.rect(infoX, infoTblY, infoW, infoRowH, 'F');
    pdf.setDrawColor(220, 220, 220);
    pdf.setLineWidth(0.12);
    pdf.rect(infoX, infoTblY, infoW, infoRowH);
    pdf.line(infoX + halfW, infoTblY, infoX + halfW, infoTblY + infoRowH * 2);
    pdf.setFontSize(7.5);
    pdf.setTextColor(80, 80, 80);
    pdf.text('型番', infoX + 1.8, infoTblY + 3.4);
    pdf.text('数量', infoX + halfW + 1.8, infoTblY + 3.4);

    // データ行
    pdf.rect(infoX, infoTblY + infoRowH, infoW, infoRowH);
    pdf.setFontSize(9);
    pdf.setTextColor(20, 20, 20);
    pdf.text(data.productSku || '', infoX + 1.8, infoTblY + infoRowH + 3.6);
    pdf.text(data.quantity.toLocaleString('ja-JP') + '台', infoX + halfW + 1.8, infoTblY + infoRowH + 3.6);

    // (2) 価格 3列表（本体価格 / カスタマイズ追加 / 税込合計）
    var priceTblY = infoTblY + infoRowH * 2 + 2;
    var hasOpt = data.optionsTotal > 0;
    var priceCols = hasOpt ? 3 : 2;
    var priceColW = infoW / priceCols;

    // ヘッダー
    pdf.setFillColor(243, 244, 246);
    pdf.rect(infoX, priceTblY, infoW, infoRowH, 'F');
    pdf.rect(infoX, priceTblY, infoW, infoRowH);
    for (var pc = 1; pc < priceCols; pc++) {
      pdf.line(infoX + priceColW * pc, priceTblY, infoX + priceColW * pc, priceTblY + infoRowH * 2);
    }
    pdf.setFontSize(7);
    pdf.setTextColor(80, 80, 80);
    pdf.text('本体価格', infoX + 1.8, priceTblY + 3.4);
    if (hasOpt) {
      pdf.text('カスタマイズ追加', infoX + priceColW + 1.8, priceTblY + 3.4);
    }
    var totalLblX = infoX + priceColW * (priceCols - 1) + 1.8;
    pdf.text('税込合計', totalLblX, priceTblY + 3.4);

    // データ行
    pdf.rect(infoX, priceTblY + infoRowH, infoW, infoRowH + 2);
    pdf.setFontSize(8.5);
    pdf.setTextColor(20, 20, 20);
    pdf.text(formatYen(data.unitPrice) + '（税込）', infoX + 1.8, priceTblY + infoRowH + 3.6);
    if (hasOpt) {
      pdf.text('+' + formatYen(data.optionsTotal) + '（税込）',
        infoX + priceColW + 1.8, priceTblY + infoRowH + 3.6);
    }
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(11);
    pdf.setTextColor(185, 28, 28);
    pdf.text(formatYen(data.grandTotal), totalLblX, priceTblY + infoRowH + 4);
    // (税込) を小さく
    var totalYenW = pdf.getTextWidth(formatYen(data.grandTotal));
    pdf.setFontSize(6.5);
    pdf.text('（税込）', totalLblX + totalYenW + 0.6, priceTblY + infoRowH + 4);

    // 価格保証ではない旨の短い注意書き
    var saleNoteY = priceTblY + infoRowH * 2 + 4.5;
    pdf.setFontSize(6.8);
    pdf.setTextColor(120, 120, 120);
    var saleNote = '※ ご注文時の販売価格が適用されます。価格保証を行う書類ではありません。';
    pdf.text(saleNote, infoX, saleNoteY);

    var summaryH = Math.max(imgBoxH, saleNoteY + 2 - summaryTop);
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.15);
    pdf.rect(M, summaryTop, contentW, summaryH);
    y = summaryTop + summaryH + 4;

    // ===== カスタマイズ内容 =====
    if (data.options.length > 0) {
      y = ensureSpace(pdf, y, 12, pageCtx);
      y = drawSectionHeading(pdf, 'カスタマイズ内容', M, y);
      y = drawOptionsTable(pdf, data.options, data.optionsTotal, M, y, contentW, pageCtx);
      y += 3;
    }

    // ===== 選択後の最終仕様（左） + 注意事項（右） の2カラム =====
    y = ensureSpace(pdf, y, 12, pageCtx);
    var twoColTop = y;
    var leftColW = contentW * 0.62;  // 約120mm
    var gapW = contentW * 0.02;
    var rightColW = contentW - leftColW - gapW;  // 約70mm
    var rightColX = M + leftColW + gapW;

    // 左: 選択後の最終仕様
    var leftY = drawSectionHeading(pdf, '選択後の最終仕様', M, twoColTop);
    leftY = drawSpecTable(pdf, data.spec, M, leftY, leftColW, pageCtx);

    // 右: 注意事項
    var rightY = drawSectionHeading(pdf, '注意事項', rightColX, twoColTop);
    // 見出し「注意事項」と本文の間に余白を確保（見出しと本文が重なるのを防ぐ）
    rightY += 2.5;
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setTextColor(70, 70, 70);
    // 注意事項は1行ずつ折り返さずに表示し、文節途中での改行崩れを防ぐ。
    // すべての行が枠幅に収まる共通フォントサイズを決める（最小6.2ptまで縮小許容）。
    var notesFs = 7;
    var notesMinFs = 6.2;
    while (notesFs > notesMinFs) {
      pdf.setFontSize(notesFs);
      var allFit = true;
      for (var ni = 0; ni < data.notes.length; ni++) {
        if (pdf.getTextWidth('・' + data.notes[ni]) > rightColW) { allFit = false; break; }
      }
      if (allFit) break;
      notesFs = Math.round((notesFs - 0.2) * 10) / 10;
    }
    pdf.setFontSize(notesFs);
    var notesLineH = Math.max(3.4, notesFs * 0.5);
    data.notes.forEach(function (n) {
      pdf.text('・' + n, rightColX, rightY);
      rightY += notesLineH;
    });

    // 右カラム内に「商品ページはこちら」+ URL + QR2つ + 補足を集約配置
    // 注意事項の下にしっかり余白を空ける
    rightY += 14;
    pdf.setDrawColor(210, 210, 210);
    pdf.setLineWidth(0.15);
    pdf.line(rightColX, rightY, rightColX + rightColW, rightY);
    rightY += 5;

    // QRコードブロックの見出し（スマホで確認）
    var safeProductUrl = sanitizeUrl(data.productUrl);
    var safeLineUrl = sanitizeUrl(LINE_URL);
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(20, 20, 20);
    pdf.text('スマホで確認', rightColX, rightY);
    rightY += 4.4;

    // URL（折り返し2行まで・欧文フォントで字間の乱れを防ぐ）
    if (safeProductUrl) {
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(30, 64, 175);
      var urlLines = wrapText(pdf, safeProductUrl, rightColW);
      var urlMaxLines = Math.min(urlLines.length, 2);
      for (var ui = 0; ui < urlMaxLines; ui++) {
        var lineText = urlLines[ui];
        if (ui === 1 && urlLines.length > 2) {
          // 続きがある場合は末尾を「…」に
          while (pdf.getTextWidth(lineText + '…') > rightColW && lineText.length > 0) {
            lineText = lineText.slice(0, -1);
          }
          lineText = lineText + '…';
        }
        pdf.text(lineText, rightColX, rightY + ui * 3.2);
      }
      try {
        pdf.link(rightColX, rightY - 2, rightColW, urlMaxLines * 3.2 + 1, { url: safeProductUrl });
      } catch (e) { /* ignore */ }
      rightY += urlMaxLines * 3.2 + 1.5;
      pdf.setFont(FONT_NAME, 'normal');
    }

    // QR 2つを右カラムの左端・右端に配置（左右に離す）
    var qrR = 18;
    var productQrX = rightColX;                          // 左端
    var lineQrX = rightColX + rightColW - qrR;           // 右端
    var productQrUrl = safeProductUrl ? generateQrDataUrl(safeProductUrl, 220) : null;
    var lineQrUrl = safeLineUrl ? generateQrDataUrl(safeLineUrl, 220) : null;
    if ((safeProductUrl && !productQrUrl) || (safeLineUrl && !lineQrUrl)) {
      showToast('QRコードを生成できなかったためURLのみ掲載します');
    }
    if (productQrUrl) {
      pdf.addImage(productQrUrl, 'PNG', productQrX, rightY, qrR, qrR);
      pdf.setFont(FONT_NAME, 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(40, 40, 40);
      var l1 = '商品ページ';
      var l1W = pdf.getTextWidth(l1);
      pdf.text(l1, productQrX + (qrR - l1W) / 2, rightY + qrR + 3.4);
    }
    if (lineQrUrl) {
      pdf.addImage(lineQrUrl, 'PNG', lineQrX, rightY, qrR, qrR);
      pdf.setFont(FONT_NAME, 'normal');
      pdf.setFontSize(7);
      pdf.setTextColor(40, 40, 40);
      var l2 = 'LINEで相談';
      var l2W = pdf.getTextWidth(l2);
      pdf.text(l2, lineQrX + (qrR - l2W) / 2, rightY + qrR + 3.4);
    }
    // QRラベル分を含めた高さ進行 + 補足文言までの余白
    rightY += qrR + 9;

    // 補足文言（QRから少し下に離す）
    pdf.setFontSize(6);
    pdf.setTextColor(110, 110, 110);
    var supplements = [
      '最新の価格・在庫状況は商品ページをご確認ください。',
      'LINEでカスタマイズ相談を受け付けています。',
    ];
    supplements.forEach(function (sp) {
      var spLines = wrapText(pdf, sp, rightColW);
      spLines.forEach(function (line) {
        pdf.text(line, rightColX, rightY);
        rightY += 3;
      });
    });

    y = Math.max(leftY, rightY) + 2;

    // 注意事項・商品URL・QRはすべて最終仕様の右カラム内に集約済み

    // 商品URL + QR + 補足は右カラム内に集約済み（上記の2カラム配置内）
    return pdf;
  }

  function drawSectionHeading(pdf, text, x, y) {
    var h = 4.5;
    pdf.setFillColor(20, 20, 20);
    pdf.rect(x, y, 1.2, h, 'F');
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(9.5);
    pdf.setTextColor(20, 20, 20);
    pdf.text(text, x + 2.4, y + 3.5);
    return y + h + 1;
  }

  /**
   * テーブルヘッダーを描画してy座標を進める。改ページ後にも再描画する用途で再利用可能。
   */
  function drawOptionsTableHeader(pdf, x, y, w, col1, col2) {
    var headerH = 5.5;
    pdf.setFillColor(31, 41, 55);
    pdf.rect(x, y, w, headerH, 'F');
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(7.8);
    pdf.setTextColor(255, 255, 255);
    // ヘッダー名（項目／選択内容／追加金額）は各列幅の中央に配置（データ行の揃えは従来どおり）
    var col3 = w - col1 - col2;
    var ty = y + 3.8;
    var hLabels = [
      { t: '項目', cx: x + col1 / 2 },
      { t: '選択内容', cx: x + col1 + col2 / 2 },
      { t: '追加金額', cx: x + col1 + col2 + col3 / 2 }
    ];
    hLabels.forEach(function (h) {
      pdf.text(h.t, h.cx - pdf.getTextWidth(h.t) / 2, ty);
    });
    return y + headerH;
  }

  // 両テーブルで統一する左列幅（カスタマイズ内容の項目列 = 最終仕様の項目列）
  // contentW = 210 - 8*2 = 194mm
  // col1 = 40mm (約20.6%) / col3 = 36mm (約18.5%) / col2 = 118mm (約60.8%)
  var TABLE_COL1_WIDTH = 40;
  var TABLE_COL3_WIDTH = 36;  // 追加金額列（カスタマイズ内容テーブルのみ使用）

  function drawOptionsTable(pdf, options, total, x, y, w, pageCtx) {
    var col1 = TABLE_COL1_WIDTH;
    var col3 = TABLE_COL3_WIDTH;
    var col2 = w - col1 - col3; // 選択内容（最広）
    var headerH = 5.5;
    var dataLineH = 3.5;
    var pad = 1.8;

    // ヘッダーをまず描画
    var headerTop = y;
    y = drawOptionsTableHeader(pdf, x, y, w, col1, col2);
    var dataTop = y;
    var sectionStartY = headerTop;

    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.12);

    options.forEach(function (o) {
      // 値テキストは fitTextToWidth で確実に col2 幅内に収める
      pdf.setFont(FONT_NAME, 'normal');
      var fit = fitTextToWidth(pdf, o.value, col2 - 3, 7.8, 5.5);
      var valueLines = fit.lines;
      var valueFontSize = fit.fontSize;
      var actualLineH = Math.max(dataLineH, valueFontSize * 0.45);
      var thisRowH = Math.max(5, valueLines.length * actualLineH + pad * 2);

      // 改ページ判定（次行が収まらないなら現在ページの枠を閉じてaddPage）
      var beforeY = y;
      var ensured = ensureSpace(pdf, y, thisRowH, pageCtx);
      if (ensured !== y) {
        pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.12);
        pdf.line(x + col1, dataTop, x + col1, beforeY);
        pdf.line(x + col1 + col2, dataTop, x + col1 + col2, beforeY);
        pdf.setDrawColor(180, 180, 180); pdf.setLineWidth(0.25);
        pdf.rect(x, sectionStartY, w, beforeY - sectionStartY);
        y = drawOptionsTableHeader(pdf, x, ensured, w, col1, col2);
        dataTop = y;
        sectionStartY = ensured;
      }

      pdf.line(x, y, x + w, y);

      // 項目名
      pdf.setFontSize(7.8);
      pdf.setTextColor(60, 60, 60);
      pdf.text(o.group || '', x + 1.8, y + pad + 2);

      // 選択内容（fitで決定したフォントサイズで描画）
      pdf.setFontSize(valueFontSize);
      pdf.setTextColor(20, 20, 20);
      valueLines.forEach(function (line, i) {
        pdf.text(line, x + col1 + 1.8, y + pad + 2 + i * actualLineH);
      });

      // 追加金額
      pdf.setFontSize(7.8);
      var priceText = o.price ? '+' + formatYen(o.price) : '−';
      var priceW = pdf.getTextWidth(priceText);
      pdf.text(priceText, x + w - priceW - 1.8, y + pad + 2);

      y += thisRowH;
    });

    // 合計行 (改ページ判定)
    var beforeTotalY = y;
    var ensuredTotal = ensureSpace(pdf, y, headerH, pageCtx);
    if (ensuredTotal !== y) {
      pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.12);
      pdf.line(x + col1, dataTop, x + col1, beforeTotalY);
      pdf.line(x + col1 + col2, dataTop, x + col1 + col2, beforeTotalY);
      pdf.setDrawColor(180, 180, 180); pdf.setLineWidth(0.25);
      pdf.rect(x, sectionStartY, w, beforeTotalY - sectionStartY);
      y = drawOptionsTableHeader(pdf, x, ensuredTotal, w, col1, col2);
      dataTop = y;
      sectionStartY = ensuredTotal;
    }

    pdf.setFillColor(243, 244, 246);
    pdf.rect(x, y, w, headerH, 'F');
    pdf.line(x, y, x + w, y);
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(20, 20, 20);
    var totalLbl = 'カスタマイズ追加金額 合計';
    var totalLblW = pdf.getTextWidth(totalLbl);
    pdf.text(totalLbl, x + col1 + col2 - totalLblW - 1.8, y + 3.8);
    pdf.setTextColor(185, 28, 28);
    pdf.setFontSize(9);
    var totalText = '+' + formatYen(total);
    var totalW = pdf.getTextWidth(totalText);
    pdf.text(totalText, x + w - totalW - 1.8, y + 3.8);
    y += headerH;

    // 縦線と外枠
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.12);
    pdf.line(x + col1, dataTop, x + col1, y);
    pdf.line(x + col1 + col2, dataTop, x + col1 + col2, y);
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.25);
    pdf.rect(x, sectionStartY, w, y - sectionStartY);

    return y;
  }

  function drawSpecTable(pdf, spec, x, y, w, pageCtx) {
    var col1 = TABLE_COL1_WIDTH;
    var rowH = 4.8;
    var dataLineH = 3.4;
    var pad = 1.4;

    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.12);
    pdf.setFont(FONT_NAME, 'normal');
    pdf.setFontSize(7.8);

    var sectionStartY = y;
    spec.forEach(function (r) {
      // 値テキストは fitTextToWidth で確実に幅内に収める
      pdf.setFont(FONT_NAME, 'normal');
      var fit = fitTextToWidth(pdf, r.value, w - col1 - 3, 7.8, 5.5);
      var valueLines = fit.lines;
      var valueFontSize = fit.fontSize;
      var actualLineH = Math.max(dataLineH, valueFontSize * 0.43);
      var thisRowH = Math.max(rowH, valueLines.length * actualLineH + pad * 2);

      // 改ページ判定
      var beforeY = y;
      var ensured = ensureSpace(pdf, y, thisRowH, pageCtx);
      if (ensured !== y) {
        pdf.setDrawColor(229, 231, 235); pdf.setLineWidth(0.12);
        pdf.line(x + col1, sectionStartY, x + col1, beforeY);
        pdf.line(x, beforeY, x + w, beforeY);
        pdf.setDrawColor(180, 180, 180); pdf.setLineWidth(0.25);
        pdf.rect(x, sectionStartY, w, beforeY - sectionStartY);
        y = ensured;
        sectionStartY = ensured;
      }

      pdf.setFillColor(243, 244, 246);
      pdf.rect(x, y, col1, thisRowH, 'F');

      pdf.line(x, y, x + w, y);

      // ラベル列
      pdf.setFontSize(7.8);
      pdf.setTextColor(55, 65, 81);
      pdf.text(r.label, x + 1.8, y + pad + 1.8);

      // 値列（fitで決定したフォントサイズで描画）
      pdf.setFontSize(valueFontSize);
      pdf.setTextColor(20, 20, 20);
      valueLines.forEach(function (line, i) {
        pdf.text(line, x + col1 + 1.8, y + pad + 1.8 + i * actualLineH);
      });
      y += thisRowH;
    });

    // 最終ページの縦線・外枠
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.12);
    pdf.line(x + col1, sectionStartY, x + col1, y);
    pdf.line(x, y, x + w, y);
    pdf.setDrawColor(180, 180, 180);
    pdf.setLineWidth(0.25);
    pdf.rect(x, sectionStartY, w, y - sectionStartY);

    return y;
  }

  // ---------- メインフロー ----------
  /**
   * jsPDFドキュメントをJPG画像に変換してダウンロードする。
   * PDFはメモリ上（arraybuffer）にのみ存在し、ファイルとしては保存しない。
   * 複数ページの場合は縦に連結して1枚のJPGにする（DLは常に1ファイル）。
   */
  async function downloadPdfAsJpeg(pdf, filename) {
    var buf = pdf.output('arraybuffer');
    var doc = await window.pdfjsLib.getDocument({ data: buf }).promise;
    var SCALE = 2.8; // 72dpi × 2.8 ≒ 200dpi相当（A4 → 約1666×2358px）
    var pageCanvases = [];
    try {
      for (var i = 1; i <= doc.numPages; i++) {
        var page = await doc.getPage(i);
        var viewport = page.getViewport({ scale: SCALE });
        var c = document.createElement('canvas');
        c.width = Math.floor(viewport.width);
        c.height = Math.floor(viewport.height);
        var ctx = c.getContext('2d');
        // JPEGは透過を持てないため背景を白で塗る
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, c.width, c.height);
        await page.render({ canvasContext: ctx, viewport: viewport }).promise;
        pageCanvases.push(c);
      }
    } finally {
      try { doc.destroy(); } catch (e) { /* ignore */ }
    }

    var out = pageCanvases[0];
    if (pageCanvases.length > 1) {
      var totalH = 0, maxW = 0;
      pageCanvases.forEach(function (pc) { totalH += pc.height; if (pc.width > maxW) maxW = pc.width; });
      out = document.createElement('canvas');
      out.width = maxW;
      out.height = totalH;
      var octx = out.getContext('2d');
      octx.fillStyle = '#ffffff';
      octx.fillRect(0, 0, maxW, totalH);
      var oy = 0;
      pageCanvases.forEach(function (pc) { octx.drawImage(pc, 0, oy); oy += pc.height; });
    }

    return new Promise(function (resolve, reject) {
      out.toBlob(function (blob) {
        if (!blob) { reject(new Error('JPEG変換に失敗しました')); return; }
        var url = URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(function () { URL.revokeObjectURL(url); }, 10000);
        resolve();
      }, 'image/jpeg', 0.92);
    });
  }

  async function handleJpgButtonClick(btn) {
    var originalLabel = btn.querySelector('.pc-spec-pdf-button__label');
    var originalText = originalLabel ? originalLabel.textContent : 'PC構成仕様書を画像で出力';
    btn.disabled = true;
    if (originalLabel) originalLabel.textContent = '生成中...';

    var stage = 'init';
    try {
      stage = 'load-libs';
      await withTimeout(loadJsPdf(), 15000, 'jspdf load');
      await withTimeout(loadPdfJs(), 15000, 'pdfjs load');

      stage = 'collect-data';
      var data = collectPdfData(btn);

      stage = 'generate-pdf';
      var sku = btn.dataset.sku || 'product';
      var filename = 'PC構成仕様書_' + sku + '_' + ymd(data.now) + '.jpg';
      var pdf = await withTimeout(renderPdfNative(data), PDF_OVERALL_TIMEOUT, 'pdf generate');

      stage = 'convert-jpeg';
      await withTimeout(downloadPdfAsJpeg(pdf, filename), 30000, 'jpeg convert');
    } catch (err) {
      console.error('[pc-spec-pdf] jpg failed at stage:', stage, err);
      alert('画像生成に失敗しました。\n段階: ' + stage + '\n詳細: ' + (err && err.message ? err.message : err));
    } finally {
      btn.disabled = false;
      if (originalLabel) originalLabel.textContent = originalText;
    }
  }

  async function handlePdfButtonClick(btn) {
    var originalLabel = btn.querySelector('.pc-spec-pdf-button__label');
    var originalText = originalLabel ? originalLabel.textContent : 'PC構成仕様書をPDFで出力';
    btn.disabled = true;
    if (originalLabel) originalLabel.textContent = '生成中...';

    var stage = 'init';
    try {
      stage = 'load-libs';
      await withTimeout(loadJsPdf(), 15000, 'jspdf load');

      stage = 'collect-data';
      var data = collectPdfData(btn);

      stage = 'generate-pdf';
      var sku = btn.dataset.sku || 'product';
      var filename = 'PC構成仕様書_' + sku + '_' + ymd(data.now) + '.pdf';
      var pdf = await withTimeout(renderPdfNative(data), PDF_OVERALL_TIMEOUT, 'pdf generate');
      pdf.save(filename);
    } catch (err) {
      console.error('[pc-spec-pdf] failed at stage:', stage, err);
      alert('PDF生成に失敗しました。\n段階: ' + stage + '\n詳細: ' + (err && err.message ? err.message : err));
    } finally {
      btn.disabled = false;
      if (originalLabel) originalLabel.textContent = originalText;
    }
  }

  document.addEventListener('click', function (e) {
    var jpgBtn = e.target.closest('[data-pc-spec-jpg-button]');
    if (jpgBtn) {
      e.preventDefault();
      handleJpgButtonClick(jpgBtn);
      return;
    }
    var btn = e.target.closest('[data-pc-spec-pdf-button]');
    if (!btn) return;
    e.preventDefault();
    handlePdfButtonClick(btn);
  });
})();
